import React, { useMemo } from "react";
import AgentDirectory, { DirectorySkeleton } from "@/components/AgentDirectory";
import { glue, slugify, townPath } from "@/components/DirectoryBreadcrumb";
import { useAgentDirectory, useDirectoryPlace } from "@/hooks/useAgentDirectory";
import AppLink from "@/components/AppLink";

export const HOME_AGENTS = 6;

export const homeSeed = (saved) =>
  saved?.city && saved?.state
    ? { city: saved.city, state: String(saved.state).toUpperCase(), location: saved.latitude ? saved : null }
    : null;

export const firstAgents = (query, limit = HOME_AGENTS) => {
  const first = query?.data?.pages?.[0];
  if (!first) return query;
  return {
    ...query,
    data: { ...query.data, pages: [{ ...first, agents: (first.agents || []).slice(0, limit) }] },
    hasNextPage: false,
  };
};

const DirectoryHomeAgents = ({ saved }) => {
  const seed = homeSeed(saved);
  const stateSlug = seed ? seed.state.toLowerCase() : "";
  const townSlug = seed ? slugify(seed.city) : "";
  const placeQuery = useDirectoryPlace(stateSlug, townSlug);
  const place = placeQuery.data?.place;
  const settled = Boolean(place) || placeQuery.isError;
  const location = useMemo(() => {
    if (place) return { city: place.name, state: seed.state, latitude: Number(place.lat), longitude: Number(place.long) };
    return placeQuery.isError ? seed?.location || null : null;
  }, [place, placeQuery.isError, seed?.state, seed?.location]);
  const query = useAgentDirectory(settled ? location : null, {}, place ? `${stateSlug}/${townSlug}` : "");

  if (!seed || (settled && !location)) return null;
  const name = place?.name || seed.city;
  const label = `${name}, ${seed.state}`;
  const agents = query.data?.pages?.[0]?.agents || [];
  if (!query.isLoading && !query.isError && query.data && !agents.length) return null;

  return (
    <section className="directory-section" aria-labelledby="directory-home-agents">
      <div className="directory-section__head">
        <h2 id="directory-home-agents">{glue(`Agents in and near ${label}`)}</h2>
        <p>From your last&nbsp;search</p>
      </div>
      {!location || query.isLoading ? (
        <DirectorySkeleton count={HOME_AGENTS} />
      ) : (
        <AgentDirectory query={firstAgents(query)} location={location} scopeLabel={label} placeName={name} />
      )}
      {place ? (
        <AppLink className="directory-section__more" href={townPath(stateSlug, townSlug)}>
          {glue(`All agents in ${label}`)}
        </AppLink>
      ) : null}
    </section>
  );
};

export default DirectoryHomeAgents;
